import type { CSSProperties } from "react";
import { ParticleSphere } from "@/components/ui/particle-sphere";

/**
 * Adapted from 21st.dev "Orbiting Circles with Globe" (shadcnspace · id 18043).
 * Agent harness icons orbit a brand particle globe.
 */

type OrbitIcon = { src: string; alt: string; angle: number };

type Orbit = {
  size: string;
  duration: number;
  icons: OrbitIcon[];
};

const orbits: Orbit[] = [
  {
    // Full 21st-scale rings (restored — not shrunk)
    size: "w-[20rem] h-[20rem] md:w-[32rem] md:h-[32rem]",
    duration: 22,
    icons: [
      { src: "/brands/claude.svg", alt: "Claude", angle: -50 },
      { src: "/brands/grok.svg", alt: "Grok", angle: 10 },
      { src: "/brands/cursor.svg", alt: "Cursor", angle: 70 },
    ],
  },
  {
    size: "w-[26rem] h-[26rem] md:w-[40rem] md:h-[40rem]",
    duration: 30,
    icons: [
      { src: "/brands/gemini.svg", alt: "Gemini", angle: -30 },
      { src: "/brands/openai.svg", alt: "OpenAI", angle: 30 },
      { src: "/brands/opencode.svg", alt: "OpenCode", angle: 90 },
      { src: "/brands/continue.svg", alt: "Continue", angle: 150 },
    ],
  },
  {
    size: "w-[32rem] h-[32rem] md:w-[48rem] md:h-[48rem]",
    duration: 38,
    icons: [
      { src: "/brands/hermes.svg", alt: "Hermes", angle: -80 },
      { src: "/brands/openclaw.svg", alt: "OpenClaw", angle: -20 },
      { src: "/brands/aider.svg", alt: "Aider", angle: 40 },
      { src: "/brands/kilo.svg", alt: "Kilo", angle: 100 },
      { src: "/brands/github-mark.svg", alt: "GitHub", angle: 160 },
    ],
  },
];

export function OrbitingCirclesGlobe({ className = "" }: { className?: string }) {
  return (
    <div
      className={`relative flex h-[26rem] w-full justify-center overflow-hidden md:h-[42rem] ${className}`}
    >
      {/* Center particle globe — sits on the bottom like the 21st demo */}
      <div className="pointer-events-none absolute bottom-0 left-1/2 z-10 aspect-square w-[20rem] -translate-x-1/2 translate-y-1/2 md:w-[36rem]">
        <ParticleSphere />
      </div>

      {/* Soft horizon fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-24 bg-gradient-to-t from-og-ink to-transparent" />

      {orbits.map((orbit, index) => {
        const isCW = index % 2 === 0;
        const orbitAnim = isCW ? "orbit-cw" : "orbit-ccw";
        const counterAnim = isCW ? "counter-cw" : "counter-ccw";

        const allIcons = [
          ...orbit.icons,
          ...orbit.icons.map((ic) => ({
            ...ic,
            angle: ic.angle + 180,
            alt: `${ic.alt}-mirror`,
          })),
        ];

        return (
          <div
            key={index}
            className={`absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rounded-full border border-white/10 ${orbit.size}`}
            style={{
              boxShadow:
                index === 0
                  ? "0 0 80px -20px rgba(249,115,22,0.25)"
                  : undefined,
            }}
          >
            {allIcons.map((iconData, iconIndex) => (
              <div
                key={`${iconData.alt}-${iconIndex}`}
                className="absolute top-0 left-1/2 -ml-7 flex h-1/2 origin-bottom flex-col items-center justify-start md:-ml-8"
                style={
                  {
                    "--start-angle": `${iconData.angle}deg`,
                    animation: `${orbitAnim} ${orbit.duration}s linear infinite`,
                  } as CSSProperties
                }
              >
                <div
                  className="relative z-10 -mt-7 rounded-full border border-white/12 bg-og-ink-elevated/90 p-2.5 shadow-[0_0_24px_-8px_rgba(249,115,22,0.45)] backdrop-blur-sm md:-mt-8 md:p-3"
                  style={
                    {
                      "--counter-offset": `${-iconData.angle}deg`,
                      animation: `${counterAnim} ${orbit.duration}s linear infinite`,
                    } as CSSProperties
                  }
                >
                  <img
                    src={iconData.src}
                    alt={iconData.alt}
                    width={32}
                    height={32}
                    className="h-5 w-5 object-contain md:h-7 md:w-7"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default OrbitingCirclesGlobe;
